import type { ReactNode, Ref } from 'react'

export interface PageHeaderProps {
  /** The screen's only `<h1>`. */
  title: ReactNode
  /** A class on the `<h1>` — `UserDetail` puts `mono-cell` on an id. */
  titleClassName?: string
  /**
   * The mono kicker above the title, rendered AS GIVEN.
   *
   * No wrapper per child and no separator logic: a string stays one text node,
   * which is what lets a caller assert on the whole kicker rather than on
   * whichever fragment a splitter happened to produce.
   */
  eyebrow?: ReactNode
  /**
   * `aria-hidden` on the eyebrow, for the four screens whose kicker repeats the
   * `<h1>` verbatim (Record, History, Baseline, Account — R25). It is a
   * decoration-marker, never a way to hide text from a contrast check.
   */
  eyebrowHidden?: boolean
  /** A non-action node beside the title — a status pill. Rendered unwrapped. */
  aside?: ReactNode
  /** Controls, in `.page__actions`, last inside `.page__head`. */
  actions?: ReactNode
  /** The sentence under the title. A node: a detail lead can hold a pill. */
  lead?: ReactNode
  /** Rendered first. The caller passes its own `<Link className="page__back">`. */
  back?: ReactNode
  /** Anything after the lead. */
  children?: ReactNode
  /** Appended after `page` — `retry-landing`, today. */
  className?: string
  /** On the `<header>`. `useRetryLanding`'s `landOn` is a callback ref. */
  ref?: Ref<HTMLElement>
  /** On the `<header>`, so the landed element can take focus. */
  tabIndex?: number
}

/**
 * Does this node render anything at all?
 *
 * `!= null` is NOT that test, and the difference reaches every screen in the
 * sprint. `actions={isOwner && <Button/>}` with `isOwner` false passes `false`,
 * and `false != null` is `true` -- so a `!= null` guard opens `.page__head` and
 * `.page__actions` around a child React draws as nothing, and the screen shows
 * an empty flex box beside the title with no markup explaining it. `cond && <X/>`
 * is the idiom the six consuming packages write, and this is the component all
 * of them compose, so the wrong test surfaces six times over and nowhere near
 * here.
 *
 * React renders nothing for exactly four values -- `null`, `undefined`, `true`
 * and `false`. `0` is not one of them (it renders "0"), which is why this is a
 * check against those four and not a truthiness check.
 */
function renders(node: ReactNode): boolean {
  return node != null && typeof node !== 'boolean'
}

/**
 * The header every screen starts with (v3, `app.css:996-1030`).
 *
 * ── WHY A COMPONENT AND NOT FOUR CLASSES ────────────────────────────────────
 *
 * Thirteen screens wrote their own `<header className="page">` with an `<h1>`
 * and sometimes a lead, and the ORDER of the parts is the thing that drifts:
 * an eyebrow below the title, a lead above it, a `.page__actions` wrapper on
 * one screen and a bare button on the next. The DOM order here is fixed and
 * `PageHeader.test.tsx` asserts it, so a screen cannot get it wrong quietly.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
 *
 * No router import, no context, no arithmetic and no comparison. `back` is a
 * node the caller builds, because a `to` prop would make this component import
 * `react-router` and every unit test that renders a header would need a
 * `MemoryRouter` around it. `aside` is rendered UNWRAPPED for the same kind of
 * reason: a `<div>` around it would put a layout box between `.page__head` and
 * a pill that is supposed to sit on the title's baseline.
 *
 * `.page__head` exists only when there is something beside the title. A bare
 * `<h1>` in a flex row with `justify-content: space-between` is the same box as
 * a bare `<h1>`, but it is one more element for every screen to step through.
 */
export function PageHeader({
  title,
  titleClassName,
  eyebrow,
  eyebrowHidden,
  aside,
  actions,
  lead,
  back,
  children,
  className,
  ref,
  tabIndex,
}: PageHeaderProps) {
  const heading = <h1 className={titleClassName}>{title}</h1>

  return (
    <header
      ref={ref}
      tabIndex={tabIndex}
      className={['page', className].filter(Boolean).join(' ')}
    >
      {back}
      {renders(eyebrow) && (
        <div className="page__eyebrow" aria-hidden={eyebrowHidden ? 'true' : undefined}>
          {eyebrow}
        </div>
      )}
      {renders(aside) || renders(actions) ? (
        <div className="page__head">
          {heading}
          {aside}
          {renders(actions) && <div className="page__actions">{actions}</div>}
        </div>
      ) : (
        heading
      )}
      {renders(lead) && <p className="page__lead">{lead}</p>}
      {children}
    </header>
  )
}
